import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import { i18n } from '@/i18n'
import type {
  ListResponse,
  ProcurementBatch,
  ProcurementBatchContainerExpenses,
  ProcurementBatchStatus,
  ProcurementContainerExpenseInput,
  ProcurementContainerExpensesInput,
  ProcurementProforma,
  ProcurementTransportJumpPreview,
} from '@/types/api'
import type { RequestOptions } from '../crud'
import { deleteBlockedError } from './deleteBlocked'
import { reportLanguage } from './reports.service'
import type { PatchProcurementTransportUnitInput, ProcurementTransportUnitItemInput } from './transportUnits.service'

export type ProcurementBatchStageChargeInput = {
  stage_number: number
  carrier_id?: string
  currency_id?: string
  /** «Итого». */
  amount?: string
  /** «Наличные», the cash part of amount. */
  cash_amount?: string
  /** «Сумма логиста»: what the logist bills the trader; empty = amount. */
  logist_amount?: string
  /** «Логист»: a partner of the logist type in the carrier's currency; empty = none. */
  logist_id?: string
  is_checked: boolean
}

export type ProcurementBatchInput = {
  proforma_id: string
  // No batch_number: the server generates it on create and it never changes.
  /** «№ инвойса покупателя». */
  invoice_number: string
  /** «№ инвойса продавца» (optional). */
  seller_invoice_number?: string
  batch_date: string
  customs_rate: string
  note?: string
  /** The sea waybill's document number («Bill of lading»). */
  bill_of_lading?: string
  /** «Дата коносамента» (YYYY-MM-DD, '' none) and «Дней» of its period. */
  bill_of_lading_date?: string
  bill_of_lading_days?: number
  // No items: a Batch starts at 0 kg and its containers and trucks allocate
  // goods from the Proforma. The API rejects planned items with 400.
  stage_charges?: ProcurementBatchStageChargeInput[]
  /** Saved with the batch in one transaction. */
  logistics?: ProcurementBatchLogisticsInput
  /**
   * A container batch's containers, saved with it in one transaction. On
   * update it is the whole list: no id creates one, an id updates the fields
   * sent, and a saved container left out is deleted.
   */
  containers?: ProcurementBatchContainerInput[]
  /**
   * «Расходы контейнеров» filled while the batch is created (ALFI TEST 506): saved and posted
   * with it in the same transaction. Each row and gross kg names its container by container_index,
   * its place in containers. Create only; a saved batch uses saveContainerExpenses.
   */
  container_expenses?: ProcurementNewContainerExpensesInput
  /** The status picked on the form: every open container moves with the save. */
  status?: { direction: 'forward' | 'reverse'; target_proforma_status_id: string; target_proforma_substatus_id: string; reason?: string }
}

export type ProcurementBatchContainerInput = {
  id?: string
  version?: number
  container_number?: string
  container_type_id?: string
  release_flag?: boolean
  items?: ProcurementTransportUnitItemInput[]
}

/** «Расходы контейнеров» of containers not saved yet: each named by its place in the request's containers. */
export type ProcurementNewContainerExpensesInput = {
  items: (Omit<ProcurementContainerExpenseInput, 'transport_unit_id'> & { container_index: number })[]
  containers: { container_index: number; gross_weight: string }[]
}

export type ProcurementBatchUpdate = ProcurementBatchInput & { version: number }

const base = '/procurement/batches'

export const procurementBatchesService = {
  path: 'procurement/batches',
  async list(query?: ListQuery, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementBatch>>(base, { params: toListParams(query), ...options })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async get(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementBatch>(`${base}/${encodeURIComponent(id)}`, options)
    return data
  },
  async create(body: ProcurementBatchInput) {
    const { data } = await http.post<ProcurementBatch>(base, body)
    return data
  },
  async update(id: string, body: ProcurementBatchUpdate) {
    const { data } = await http.put<ProcurementBatch>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  /** The batch with its logistics and containers, saved as one thing. */
  async patch(id: string, body: Partial<ProcurementBatchInput> & { version: number }) {
    const { data } = await http.patch<ProcurementBatch>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  async remove(id: string) {
    const [current, check] = await Promise.all([
      this.get(id),
      http.get<{ can_delete: boolean; reasons: string[] }>(`${base}/${encodeURIComponent(id)}/delete-check`).then((response) => response.data),
    ])
    if (!check.can_delete) throw deleteBlockedError(check.reasons, 412, 'FAILED_PRECONDITION')
    await http.delete(`${base}/${encodeURIComponent(id)}`, { params: { version: current.version } })
  },
  async proformaOptions(eligibleForBatch = true, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementProforma>>('/procurement/proformas/options', {
      params: { eligible_for_batch: eligibleForBatch, limit: 1000 },
      ...options,
    })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  /** The common status of the batch's open containers and where they can all move. */
  async status(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementBatchStatus>(`${base}/${encodeURIComponent(id)}/status`, options)
    return data
  },
  /** What moving every container of the batch to the target posts; nothing is saved. */
  async previewJump(id: string, body: ProcurementBatchStatusMove, options?: RequestOptions) {
    const { data } = await http.post<ProcurementTransportJumpPreview>(`${base}/${encodeURIComponent(id)}/status-transitions/preview`, body, {
      params: { lang: reportLanguage(i18n.locale) },
      ...options,
    })
    return data
  },
  /** Moves every open container of the batch to the target (back needs a reason). */
  async move(id: string, direction: 'forward' | 'reverse', body: ProcurementBatchStatusMove) {
    const { data } = await http.post<ProcurementBatchStatus>(`${base}/${encodeURIComponent(id)}/status-transitions/${direction}`, body)
    return data
  },
  /** «Расходы контейнеров»: the batch's containers and their port expenses. */
  async containerExpenses(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementBatchContainerExpenses>(`${base}/${encodeURIComponent(id)}/container-expenses`, options)
    return data
  },
  /** Saves every port expense of the batch and the containers' gross kg; each saved row is posted at once. */
  async saveContainerExpenses(id: string, body: ProcurementContainerExpensesInput) {
    const { data } = await http.put<ProcurementBatchContainerExpenses>(`${base}/${encodeURIComponent(id)}/container-expenses`, body)
    return data
  },
  /** The planned stage charges and water dates every open container of the batch shares. */
  async updateLogistics(id: string, body: ProcurementBatchLogisticsInput) {
    const { data } = await http.patch<ProcurementBatchStatus>(`${base}/${encodeURIComponent(id)}/logistics`, body)
    return data
  },
}

export type ProcurementBatchStatusMove = {
  target_proforma_status_id: string
  target_proforma_substatus_id: string
  reason?: string
}

export type ProcurementBatchLogisticsInput = Omit<PatchProcurementTransportUnitInput, 'version' | 'items' | 'heads' | 'trailers' | 'transport_date'> & {
  status_plans?: { proforma_status_id: string; planned_date: string }[]
}
